// app/components/AdvancedChart/hooks/useInitializeCharts.ts
import { useEffect, useRef } from 'react'
import {
  createChart,
  CandlestickSeries,
  HistogramSeries,
  IChartApi,
  ISeriesApi,
  UTCTimestamp,
  createTextWatermark
} from 'lightweight-charts'
import { MAIN_CHART_CONFIG } from '../constants/chartConfig'
import { syncTimeScale } from '../utils/syncTimeScale'
import { formatPrice } from '../utils/priceFormatter'

interface UseInitializeChartsProps {
  currentSymbol: string;
  mainChartContainer: React.RefObject<HTMLDivElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  popupRef: React.RefObject<HTMLDivElement | null>;
  popupVisible: boolean;
  setPopupVisible: (visible: boolean) => void;
  setPrice: (price: number) => void;
  setPercentChange: (text: string) => void;
  setPopupX: (x: number) => void;
  setPopupY: (y: number) => void;
  lastPriceRef: React.MutableRefObject<number | null>;
  onMouseLeave: () => void;
  onMouseEnter: () => void;
  handleResize: () => void;
  theme?: 'dark' | 'light';
  chartDataLocal: any[];
  setOhlcData: (data: { open: number; high: number; low: number; close: number } | null) => void;
}

export function useInitializeCharts({
  currentSymbol,
  mainChartContainer,
  containerRef,
  popupRef,
  popupVisible,
  setPopupVisible,
  setPrice,
  setPercentChange,
  setPopupX,
  setPopupY,
  lastPriceRef,
  onMouseLeave,
  onMouseEnter,
  handleResize,
  theme,
  chartDataLocal,
  setOhlcData
}: UseInitializeChartsProps) {
  // Create refs to hold our chart API and series.
  const chartRef = useRef<IChartApi | null>(null)
  const mainSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null)
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null)
  
  // Create a ref to hold the current chart data so crosshair listener always has latest data
  const chartDataRef = useRef(chartDataLocal)
  
  // Update the ref whenever chartDataLocal changes
  useEffect(() => {
    chartDataRef.current = chartDataLocal
  }, [chartDataLocal])

  // Clear OHLC data when symbol changes to prevent stale data
  useEffect(() => {
    setOhlcData(null);
  }, [currentSymbol, setOhlcData]);
  
  useEffect(() => {
    // Cleanup any existing chart.
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
      mainSeriesRef.current = null;
      volumeSeriesRef.current = null;
    }
    if (!mainChartContainer.current) return;

    // Get theme flag from props or fallback to document class
    let isDark = document.documentElement.classList.contains('dark')
    // If theme is explicitly provided, use that instead
    if (theme === 'dark') {
      isDark = true
    } else if (theme === 'light') {
      isDark = false
    }
    // Create the chart using MAIN_CHART_CONFIG options.
    const options = MAIN_CHART_CONFIG(isDark)
    chartRef.current = createChart(mainChartContainer.current, options)

    // Add the main candlestick series using the new v5 API.
    mainSeriesRef.current = chartRef.current.addSeries(CandlestickSeries, {
      upColor: '#2DBD85',
      downColor: '#F6465D',
      wickUpColor: '#2DBD85',
      wickDownColor: '#F6465D'
    })

    // Add the volume series on a separate price scale.
    volumeSeriesRef.current = chartRef.current.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume' // Assign a unique id for volume's price scale.
    })
    volumeSeriesRef.current.priceScale().applyOptions({
      visible: false, // Hide the volume price scale.
      scaleMargins: { top: 0.8, bottom: 0 }
    })

    // Synchronize time scales if needed.
    syncTimeScale(chartRef.current, chartRef.current)

    // **Add the watermark on the main pane**
    if (chartRef.current) {
      // Get the first pane.
      const firstPane = chartRef.current.panes()[0]
      // Check the watermark config from the options.
      if (options.watermark && options.watermark.visible) {
        createTextWatermark(firstPane, {
          horzAlign: 'center',
          vertAlign: 'center',
          lines: [
            {
              text: options.watermark.text,
              color: options.watermark.color,
              fontSize: options.watermark.fontSize,
              fontFamily: options.watermark.fontFamily,
              fontStyle: options.watermark.fontStyle,
            }
          ]
        })
      }
    }

    // Set up crosshair event on the main series.
    if (chartRef.current && mainSeriesRef.current) {
      // Add dynamic crosshair label visibility based on pane
      chartRef.current.subscribeCrosshairMove(param => {
        // Show time scale labels (vertLine) always, but price scale labels (horzLine) only on indicator panes
        const isIndicatorPane = param.paneIndex !== undefined && param.paneIndex > 0;
        chartRef.current?.applyOptions({
          crosshair: {
            horzLine: {
              labelVisible: isIndicatorPane // Show price scale labels only on indicator panes
            }
          }
        });
      });
      
      chartRef.current.subscribeCrosshairMove(param => {
        if (!param.point) {
          setOhlcData(null);
          return;
        }
        // Only handle crosshair events from the main chart pane (pane index 0)
        if (param.paneIndex !== 0) {
          setOhlcData(null);
          return;
        }
        if (!popupVisible) {
          setPopupVisible(true);
        }
        const currentPrice = mainSeriesRef.current?.coordinateToPrice(param.point.y);
        if (currentPrice == null) {
          setOhlcData(null);
          return;
        }
        setPrice(currentPrice);
        
        // Find OHLC data for the current time position
        if (param.time && param.seriesData && mainSeriesRef.current) {
          // Check if we have series data for the main candlestick series
          const mainSeriesData = param.seriesData.get(mainSeriesRef.current);
          
          if (mainSeriesData && typeof mainSeriesData === 'object' && 'open' in mainSeriesData) {
            setOhlcData({
              open: mainSeriesData.open,
              high: mainSeriesData.high,
              low: mainSeriesData.low,
              close: mainSeriesData.close
            });
          } else {
            // Fallback to chartDataRef approach
            if (chartDataRef.current && chartDataRef.current.length > 0) {
              const timeValue = typeof param.time === 'number' ? param.time : Math.floor(new Date(param.time as string).getTime() / 1000);
              
              // Find the closest candle by finding the one with the smallest time difference
              let closestCandle = null;
              let minTimeDiff = Infinity;
              
              for (const candle of chartDataRef.current) {
                const candleTime = typeof candle.time === 'number' ? candle.time : Math.floor(new Date(candle.time).getTime() / 1000);
                const timeDiff = Math.abs(candleTime - timeValue);
                
                if (timeDiff < minTimeDiff) {
                  minTimeDiff = timeDiff;
                  closestCandle = candle;
                }
              }
              
              if (closestCandle) {
                setOhlcData({
                  open: closestCandle.open,
                  high: closestCandle.high,
                  low: closestCandle.low,
                  close: closestCandle.close
                });
              } else {
                setOhlcData(null);
              }
            } else {
              setOhlcData(null);
            }
          }
        } else {
          setOhlcData(null);
        }
        
        if (lastPriceRef.current != null && lastPriceRef.current !== 0) {
          const pc = ((currentPrice - lastPriceRef.current) / lastPriceRef.current) * 100;
          const sign = pc >= 0 ? '+' : '';
          setPercentChange(`(${sign}${pc.toFixed(2)}%)`);
        } else {
          setPercentChange('');
        }
        const chartW = mainChartContainer.current?.clientWidth ?? 300;
        const popupW = 200;
        const x = chartW - popupW - 10;
        const popupHeight = popupRef.current ? popupRef.current.offsetHeight : 60;
        const y = param.point.y - popupHeight / 2;
        setPopupX(x);
        setPopupY(y);
      });
    }

    // Attach mouse and resize event listeners.
    const containerEl = containerRef.current;
    if (containerEl) {
      containerEl.addEventListener('mouseleave', onMouseLeave);
      containerEl.addEventListener('mouseenter', onMouseEnter);
    }
    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });
    if (containerEl) {
      resizeObserver.observe(containerEl);
    }
    handleResize();

    return () => {
      if (containerEl) {
        containerEl.removeEventListener('mouseleave', onMouseLeave);
        containerEl.removeEventListener('mouseenter', onMouseEnter);
        resizeObserver.unobserve(containerEl);
      }
    };
  }, [currentSymbol]);

  return {
    chartRef,
    mainSeriesRef,
    volumeSeriesRef
  };
}
